// ── Keybind Editor ──
// Settings panel for viewing and rebinding all game keybinds.
// Grouped by category with per-row rebind buttons and conflict feedback.
// Designed to be embedded inside settings-menu.ts as the "Controls" section.

import { keybindManager, KeybindManager, type Keybind } from '../core/keybinds';

const CATEGORIES = ['Movement', 'Combat', 'Weapons', 'Utility'] as const;

export class KeybindEditor {
  private container: HTMLElement;
  private listeningAction: string | null = null;
  private listeningBtn: HTMLElement | null = null;
  private boundKeyListener: ((e: KeyboardEvent) => void) | null = null;
  private boundMouseListener: ((e: MouseEvent) => void) | null = null;
  private conflictBanner: HTMLElement | null = null;
  private changeListener: (() => void) | null = null;
  // F-UI-006: Track the auto-hide timer so dispose() can cancel it.
  private bannerTimeoutId: ReturnType<typeof setTimeout> | null = null;

  constructor() {
    this.container = document.createElement('div');
    this.container.id = 'keybind-editor';
    this.build();

    // Re-render when keybinds change externally
    this.changeListener = () => this.build();
    keybindManager.onChange(this.changeListener);
  }

  /** Returns the root element for embedding in a parent panel. */
  getElement(): HTMLElement {
    return this.container;
  }

  dispose(): void {
    this.cancelListening();
    // F-UI-006: Cancel any pending banner auto-hide timeout.
    if (this.bannerTimeoutId !== null) {
      clearTimeout(this.bannerTimeoutId);
      this.bannerTimeoutId = null;
    }
    if (this.changeListener) {
      keybindManager.offChange(this.changeListener);
      this.changeListener = null;
    }
  }

  // ── Build ──

  private build(): void {
    this.container.innerHTML = '';

    // Conflict banner (hidden by default)
    this.conflictBanner = document.createElement('div');
    this.conflictBanner.style.cssText =
      'display: none; padding: 8px 12px; margin-bottom: 12px; border-radius: 3px; ' +
      'background: rgba(233,69,96,0.15); border: 1px solid rgba(233,69,96,0.4); ' +
      'color: #e94560; font-size: 11px; letter-spacing: 1px; text-align: center;';
    this.container.appendChild(this.conflictBanner);

    for (const category of CATEGORIES) {
      const binds = keybindManager.getByCategory(category);
      if (binds.length === 0) continue;

      // Category header
      const header = document.createElement('div');
      header.style.cssText =
        'font-size: 12px; color: #888; letter-spacing: 2px; text-transform: uppercase; ' +
        'margin-bottom: 8px; margin-top: 16px;';
      header.textContent = category;
      this.container.appendChild(header);

      // Rows
      for (const bind of binds) {
        this.container.appendChild(this.buildRow(bind));
      }
    }

    // Reset to Defaults button
    const resetWrap = document.createElement('div');
    resetWrap.style.cssText = 'text-align: center; margin-top: 20px; padding-top: 14px; border-top: 1px solid #2a2a2a;';
    const resetBtn = document.createElement('div');
    resetBtn.textContent = 'RESET TO DEFAULTS';
    resetBtn.style.cssText =
      'display: inline-block; padding: 8px 24px; font-size: 12px; font-weight: bold; ' +
      'letter-spacing: 2px; cursor: pointer; border-radius: 3px; transition: all 0.15s; ' +
      'background: rgba(60,60,50,0.4); border: 1px solid #3a3a2a; color: #888;';
    resetBtn.addEventListener('mouseenter', () => {
      resetBtn.style.background = 'rgba(233,69,96,0.2)';
      resetBtn.style.borderColor = '#e94560';
      resetBtn.style.color = '#e94560';
    });
    resetBtn.addEventListener('mouseleave', () => {
      resetBtn.style.background = 'rgba(60,60,50,0.4)';
      resetBtn.style.borderColor = '#3a3a2a';
      resetBtn.style.color = '#888';
    });
    resetBtn.addEventListener('click', () => {
      keybindManager.resetToDefaults();
      // build() will be called by the change listener
    });
    resetWrap.appendChild(resetBtn);
    this.container.appendChild(resetWrap);
  }

  private buildRow(bind: Keybind): HTMLElement {
    const row = document.createElement('div');
    row.style.cssText =
      'display: flex; justify-content: space-between; align-items: center; ' +
      'padding: 6px 8px; border-bottom: 1px solid rgba(42,42,42,0.5); ' +
      'transition: background 0.1s;';

    // Action name
    const label = document.createElement('span');
    label.style.cssText = 'font-size: 12px; color: #aaa; flex: 1;';
    label.textContent = bind.displayName;
    row.appendChild(label);

    // Current key display + rebind button
    const keyBtn = document.createElement('div');
    keyBtn.dataset.action = bind.action;
    keyBtn.style.cssText =
      'min-width: 100px; padding: 5px 12px; text-align: center; ' +
      'font-size: 12px; font-weight: bold; letter-spacing: 1px; ' +
      'cursor: pointer; border-radius: 3px; transition: all 0.15s; ' +
      'background: rgba(60,60,50,0.4); border: 1px solid #3a3a2a; color: #c8c090;';
    keyBtn.textContent = KeybindManager.formatKeyName(bind.key);

    keyBtn.addEventListener('mouseenter', () => {
      if (this.listeningAction !== bind.action) {
        keyBtn.style.borderColor = '#5a6a3a';
      }
    });
    keyBtn.addEventListener('mouseleave', () => {
      if (this.listeningAction !== bind.action) {
        keyBtn.style.borderColor = '#3a3a2a';
      }
    });
    keyBtn.addEventListener('click', (e) => {
      e.stopPropagation();
      this.startListening(bind.action, keyBtn);
    });

    row.appendChild(keyBtn);
    return row;
  }

  // ── Rebind listening ──

  private startListening(action: string, btn: HTMLElement): void {
    // Cancel any previous listening
    this.cancelListening();

    this.listeningAction = action;
    this.listeningBtn = btn;

    // Visual feedback
    btn.textContent = 'Press a key...';
    btn.style.background = 'rgba(233,69,96,0.2)';
    btn.style.borderColor = '#e94560';
    btn.style.color = '#e94560';

    // Hide previous conflict banner
    if (this.conflictBanner) {
      this.conflictBanner.style.display = 'none';
    }

    // Listen for next key or mouse button
    this.boundKeyListener = (e: KeyboardEvent) => {
      e.preventDefault();
      e.stopPropagation();

      // Escape cancels rebind
      if (e.code === 'Escape') {
        this.cancelListening();
        return;
      }

      this.finishRebind(e.code);
    };

    this.boundMouseListener = (e: MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      this.finishRebind(`Mouse${e.button}`);
    };

    // Use capture phase so we intercept before other handlers
    document.addEventListener('keydown', this.boundKeyListener, true);
    document.addEventListener('mousedown', this.boundMouseListener, true);
  }

  private finishRebind(newKey: string): void {
    if (!this.listeningAction) return;

    const result = keybindManager.rebind(this.listeningAction, newKey);

    if (result.success && result.conflict && this.conflictBanner) {
      this.conflictBanner.textContent =
        `Swapped: ${result.conflict.displayName} is now ${KeybindManager.formatKeyName(result.conflict.key)}`;
      this.conflictBanner.style.display = 'block';
      // F-UI-006: Track the auto-hide timer; clear any prior pending timeout
      // so rapid rebinds don't stack hides.
      if (this.bannerTimeoutId !== null) clearTimeout(this.bannerTimeoutId);
      this.bannerTimeoutId = setTimeout(() => {
        this.bannerTimeoutId = null;
        if (this.conflictBanner) this.conflictBanner.style.display = 'none';
      }, 3000);
    }

    this.cancelListening();
    // build() will be called by the change listener since rebind triggers notifyListeners
  }

  private cancelListening(): void {
    if (this.boundKeyListener) {
      document.removeEventListener('keydown', this.boundKeyListener, true);
      this.boundKeyListener = null;
    }
    if (this.boundMouseListener) {
      document.removeEventListener('mousedown', this.boundMouseListener, true);
      this.boundMouseListener = null;
    }

    // Restore button visual if we were listening
    if (this.listeningBtn && this.listeningAction) {
      const bind = keybindManager.getKeybind(this.listeningAction);
      if (bind) {
        this.listeningBtn.textContent = KeybindManager.formatKeyName(bind.key);
        this.listeningBtn.style.background = 'rgba(60,60,50,0.4)';
        this.listeningBtn.style.borderColor = '#3a3a2a';
        this.listeningBtn.style.color = '#c8c090';
      }
    }

    this.listeningAction = null;
    this.listeningBtn = null;
  }
}
